import React from 'react';
import LegalPageLayout from '@/components/legal/LegalPageLayout';

const SectionHeading = ({ children }) => (
  <h2 className="font-serif-display text-2xl text-[#14213D] mt-10 mb-3">{children}</h2>
);

export default function TermsPage() {
  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="February 11, 2026">
      <p>
        Welcome to Atlas History. These Terms of Service (&ldquo;<strong>Terms</strong>&rdquo;)
        govern your access to and use of atlashistory.org and the related services
        (collectively, the &ldquo;<strong>Service</strong>&rdquo;), operated by Chase A. Casteel
        (&ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>,&rdquo; &ldquo;<strong>our</strong>&rdquo;).
        By creating an account or using the Service, you agree to these Terms.
      </p>

      <SectionHeading>1. Eligibility</SectionHeading>
      <p>
        You must be at least 13 years old to create an individual account. Students under 13
        may use the Service only when an account is created on their behalf by a teacher or
        administrator with the appropriate consent under FERPA / COPPA.
      </p>

      <SectionHeading>2. Accounts</SectionHeading>
      <ul>
        <li>You are responsible for the activity on your account.</li>
        <li>Keep your password secure and do not share your login.</li>
        <li>Notify us immediately at the contact email below if you suspect unauthorized access.</li>
        <li>An administrator may suspend or disable an account that violates these Terms.</li>
      </ul>

      <SectionHeading>3. Subscription plans and billing</SectionHeading>
      <p>
        Atlas History offers a free tier and paid subscription tiers (Basic, Pro). Paid plans
        are billed monthly through Stripe.
      </p>
      <ul>
        <li><strong>Auto-renewal.</strong> Subscriptions renew automatically each month at the
          then-current price. You can cancel anytime from your account settings — your access
          to paid features continues until the end of the current billing period, then reverts
          to the free tier.</li>
        <li><strong>Refunds.</strong> We do not offer pro-rated refunds for partial months. If
          you believe you were charged in error, email us within 14 days and we will
          investigate.</li>
        <li><strong>Price changes.</strong> We will email active subscribers at least 30 days
          before any price change takes effect.</li>
        <li><strong>Failed payments.</strong> If a charge fails, your account may be downgraded
          to the free tier until payment is updated.</li>
      </ul>

      <SectionHeading>4. Content and intellectual property</SectionHeading>
      <p>
        <strong>Our content.</strong> Atlas History&apos;s curriculum units, primary-source
        annotations, lesson plans, rubrics, and original prose are the intellectual property of
        Chase A. Casteel and are licensed to subscribers for use in their own classroom only.
      </p>
      <p>
        You may print, project, and distribute curriculum materials to students within the
        classes you teach. You may <strong>not</strong>:
      </p>
      <ul>
        <li>Resell, sublicense, or redistribute Atlas History content to other teachers or
          schools without a separate license</li>
        <li>Publish the materials publicly (e.g., post entire units to a public website or
          file-sharing platform)</li>
        <li>Use the materials to train commercial AI models</li>
        <li>Scrape, mirror, or programmatically download bulk portions of the Service</li>
      </ul>
      <p>
        <strong>Public-domain primary sources.</strong> Where Atlas History incorporates
        government documents, historical texts, or other public-domain materials, those sources
        remain in the public domain. Our annotations and commentary on them, however, are
        copyrighted.
      </p>
      <p>
        <strong>Your content.</strong> Annotations, essay submissions, and student work that
        you create through the Service remain yours. By submitting content, you grant Atlas
        History a limited license to store, display, and process that content for the purpose
        of providing the Service to you and your students.
      </p>

      <SectionHeading>5. Acceptable use</SectionHeading>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for any illegal purpose or in violation of local, state, federal, or
          international law</li>
        <li>Attempt to gain unauthorized access to other users&apos; accounts</li>
        <li>Probe, scan, or test the vulnerability of the Service without prior written consent</li>
        <li>Submit content that infringes copyright, harasses other users, or is defamatory</li>
        <li>Use automated tools (bots, scrapers) to collect data from the Service</li>
      </ul>

      <SectionHeading>6. AI-generated content</SectionHeading>
      <p>
        Some features (lesson plan generator, essay grader, primary-source excerpt generator)
        use AI models to produce draft content. AI output may be inaccurate, biased, or
        out of date. <strong>You are responsible for reviewing AI-generated content before
        sharing it with students.</strong> Atlas History does not warrant the accuracy of AI
        output.
      </p>

      <SectionHeading>7. Service availability and modifications</SectionHeading>
      <p>
        We aim for high availability but do not guarantee uninterrupted access. We may modify,
        suspend, or discontinue any feature at any time. Material changes affecting paid plans
        will be communicated by email at least 30 days in advance.
      </p>

      <SectionHeading>8. Disclaimers</SectionHeading>
      <p>
        The Service is provided <strong>&ldquo;as is&rdquo;</strong> without warranty of any
        kind, express or implied, including but not limited to merchantability, fitness for a
        particular purpose, and non-infringement. We do not warrant that the Service will be
        error-free or uninterrupted, or that any content will be accurate or reliable.
      </p>

      <SectionHeading>9. Limitation of liability</SectionHeading>
      <p>
        To the maximum extent permitted by law, Atlas History and its operator shall not be
        liable for any indirect, incidental, special, consequential, or punitive damages, or
        any loss of profits, revenue, data, or goodwill, arising out of or related to your use
        of the Service. Our aggregate liability for any claim arising from these Terms shall
        not exceed the amount you paid us in the 12 months preceding the claim.
      </p>

      <SectionHeading>10. Termination</SectionHeading>
      <p>
        You may terminate your account at any time by emailing the contact below. We may
        suspend or terminate your access if you violate these Terms. Upon termination, your
        right to use the Service ends, but Sections 4, 8, 9, 11, and 12 of these Terms survive.
      </p>

      <SectionHeading>11. Governing law</SectionHeading>
      <p>
        These Terms are governed by the laws of the State of Kansas, USA, without regard to
        conflict-of-law principles. Any dispute arising from these Terms shall be brought
        exclusively in the state or federal courts located in Kansas.
      </p>

      <SectionHeading>12. Changes to these Terms</SectionHeading>
      <p>
        We may update these Terms from time to time. Material changes will be emailed to
        active subscribers and posted with an updated &ldquo;last updated&rdquo; date.
        Continued use of the Service after changes take effect constitutes acceptance.
      </p>

      <SectionHeading>13. Contact</SectionHeading>
      <p>
        Questions about these Terms? Email us at{' '}
        <a href="mailto:support@atlashistory.org">support@atlashistory.org</a>.
      </p>
    </LegalPageLayout>
  );
}
